import React, { useEffect, useRef, useState } from "react";
import {
  User,
  MapPin,
  Phone,
  Mail,
  StickyNote,
  PaintBucket,
  AlertCircle,
  TimerReset,
  BadgeCheck,
  ImagePlus,
  X as XIcon,
} from "lucide-react";
import { supabase } from "../../supabaseClient";
import { getStage } from "../../config/pipelineStages";
import { formatMoneyDisplay } from "../../utils/money";
import {
  getLeadTabStorageKey,
  normalizeLeadDetailTab,
} from "../../utils/tradieWorkspace";
import { LEAD_UNLOCK_COST } from "../../hooks/useCredits";
import { useToast } from "../../hooks/useToast";
import Card from "../common/Card";
import Button from "../common/Button";
import LeadChat, { IconFile, IconAudio } from "../common/LeadChat";
import QuoteBuilder from "./QuoteBuilder";
import ActivityTimeline from "./ActivityTimeline";
import InvoiceBuilder from "./InvoiceBuilder";

// ──────────────────────────────────────────────────────────────────────────
// Small helper — renders the "Wed, 7 May 2026" date label on the booking card
// ──────────────────────────────────────────────────────────────────────────
function formatBookedDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-AU", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

function isCashPaymentLead(lead) {
  const markerText = `${lead?.form?.notes || ""} ${lead?.quote?.notes || ""}`;
  return (
    lead?.form?.cashPayment === true ||
    /cash payment option selected/i.test(markerText)
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Customer-photo lightbox — arrow-key navigable.
// Used only within the Details tab when a customer photo is clicked.
// ──────────────────────────────────────────────────────────────────────────
function CustomerPhotoLightbox({ photos, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + photos.length) % photos.length);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);

  const photo = photos[index];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: "16px", right: "20px",
          background: "none", border: "none", color: "#fff",
          fontSize: "2rem", lineHeight: 1, cursor: "pointer", zIndex: 1,
        }}
        aria-label="Close"
      >
        ×
      </button>

      {photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + photos.length) % photos.length); }}
          style={{
            position: "absolute", left: "16px",
            background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
            fontSize: "1.75rem", lineHeight: 1, padding: "8px 14px",
            borderRadius: "8px", cursor: "pointer",
          }}
          aria-label="Previous photo"
        >
          ‹
        </button>
      )}

      <img
        src={photo.dataUrl}
        alt={photo.name}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw", maxHeight: "88vh",
          borderRadius: "8px", objectFit: "contain",
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        }}
      />

      {photos.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % photos.length); }}
          style={{
            position: "absolute", right: "16px",
            background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
            fontSize: "1.75rem", lineHeight: 1, padding: "8px 14px",
            borderRadius: "8px", cursor: "pointer",
          }}
          aria-label="Next photo"
        >
          ›
        </button>
      )}

      {photos.length > 1 && (
        <div style={{
          position: "absolute", bottom: "16px",
          color: "rgba(255,255,255,0.7)", fontSize: "0.875rem",
        }}>
          {index + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Chat-media lightbox — view-only. Used from the Media tab.
// position:fixed + high zIndex so it renders above everything else.
// ──────────────────────────────────────────────────────────────────────────
function MediaFileLightbox({ item, onClose }) {
  const isVideo = item.file_type?.startsWith("video/");

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10100,
        background: "rgba(0,0,0,0.88)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close lightbox"
        style={{
          position: "absolute", top: "16px", right: "20px",
          background: "none", border: "none", color: "#fff",
          fontSize: "2rem", lineHeight: 1, cursor: "pointer", zIndex: 1,
        }}
      >×</button>

      {isVideo ? (
        <video
          controls
          src={item.file_url}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: "90vw",
            maxHeight: "88vh",
            borderRadius: "8px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
          }}
        />
      ) : (
        <img
          src={item.file_url}
          alt={item.file_name || "Image"}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: "90vw",
            maxHeight: "88vh",
            borderRadius: "8px",
            objectFit: "contain",
            boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Status button grid — 5 lead statuses. Extracted for readability; each
// status maps to a fixed colour + match-values list so the active pill lights
// up correctly regardless of casing or legacy values.
// ──────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────
// Locked preview (lead not yet unlocked) — shows blurred contact fields and
// an "Unlock" CTA. Extracted so the unlocked case isn't nested inside an
// if/else fork hundreds of lines long.
// ──────────────────────────────────────────────────────────────────────────
function LockedLeadPreview({ lead, onUnlock }) {
  return (
    <>
      <div className="stats-grid">
        <div className="stat-box">
          <MapPin size={16} />
          <div className="muted small">Location</div>
          <div className="feature-title">{lead.suburb}{lead.postcode ? ` ${lead.postcode}` : ""}</div>
        </div>
        <div className="stat-box">
          <PaintBucket size={16} />
          <div className="muted small">Scope</div>
          <div className="feature-title">{lead.areas}</div>
        </div>
        <div className="stat-box">
          <AlertCircle size={16} />
          <div className="muted small">Condition</div>
          <div className="feature-title">{lead.complexity}</div>
        </div>
        <div className="stat-box">
          <TimerReset size={16} />
          <div className="muted small">Lead access</div>
          <div className="feature-title">
            {lead.soldOut
              ? "Fully quoted"
              : lead.soldCount === 0
                ? "No tradies yet — be first"
                : lead.remainingSlots === 1
                  ? "Last spot available"
                  : `${lead.remainingSlots} spots open`}
          </div>
        </div>
      </div>

      <Card className="locked-preview">
        <h3>Lead preview</h3>
        <p className="muted">
          You can already see the job type, suburb, photos count, and
          scope summary. Unlock to reveal customer contact details,
          uploaded photos, and quoting tools.
        </p>
        <p className="muted">
          <strong>Availability:</strong>{" "}
          {lead.soldOut
            ? "This job has been fully quoted."
            : lead.soldCount === 0
              ? "No one has quoted yet — you'd be the first tradie to respond."
              : lead.remainingSlots === 1
                ? "2 tradies have already quoted — only 1 spot left."
                : `${lead.soldCount} tradie${lead.soldCount === 1 ? " has" : "s have"} quoted — ${lead.remainingSlots} spots remaining.`}
        </p>

        <div className="locked-grid">
          <div className="locked-field">
            <div className="muted small">Phone</div>
            <div className="blurred-text">04xx xxx xxx</div>
          </div>
          <div className="locked-field">
            <div className="muted small">Email</div>
            <div className="blurred-text">customer@email.com</div>
          </div>
          <div className="locked-field full">
            <div className="muted small">Address</div>
            <div className="blurred-text">123 Example Street</div>
          </div>
        </div>

        <div className="button-row">
          <Button onClick={() => onUnlock(lead.id)} disabled={lead.soldOut}>
            {lead.soldOut ? "Lead sold out" : `Unlock lead ~ ${LEAD_UNLOCK_COST} credits`}
          </Button>
        </div>
      </Card>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Captured-info card (Details tab). Shows the contact + project info the
// customer submitted. Photo clicks open CustomerPhotoLightbox.
//
// NB: all contact fields below come from `lead.form`, which `useLeads.js`
// re-hydrates from the underlying `leads` table after unlock — so values
// like `form.phone` / `form.email` / `form.address` are now the real
// customer details (not the masked `leads_marketplace` view).
// ──────────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────────
// DetailHeader — compact, Trello-card-style top strip:
//   name + stage badge in row 1
//   click-to-call / mailto / map-search quick actions in row 2
// ──────────────────────────────────────────────────────────────────────────
function DetailHeader({ selected, selectedIsUnlocked }) {
  const phone = selected?.form?.phone || "";
  const email = selected?.form?.email || "";
  const fullAddress = selected?.form?.address || selected?.suburb || "";
  const stage = getStage(selected?.status || "New");

  const customerLabel = selectedIsUnlocked
    ? selected?.customer || (
        <span style={{ color: "var(--text-muted, #6b7280)", fontWeight: 400 }}>
          Loading…
        </span>
      )
    : (
        <span style={{ color: "var(--text-muted, #6b7280)", fontStyle: "italic", fontWeight: 500 }}>
          🔒 Locked lead
        </span>
      );

  return (
    <div style={{ marginBottom: "14px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "6px",
        }}
      >
        <h2 style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "1.15rem" }}>
          <User size={18} aria-hidden="true" /> {customerLabel}
        </h2>
        {selectedIsUnlocked && stage && (
          <span
            aria-label={`Stage: ${stage.label}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "3px 10px",
              borderRadius: "999px",
              background: stage.bg,
              border: `1px solid ${stage.color}55`,
              color: stage.color,
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: stage.color,
              }}
            />
            {stage.label}
          </span>
        )}
      </div>

      <p className="muted" style={{ margin: "0 0 10px", fontSize: "0.82rem" }}>
        {[selected?.trade, selected?.type].filter(Boolean).join(" · ")}
        {selected?.suburb ? ` ~ ${selected.suburb}` : ""}
        {selected?.postcode ? ` ${selected.postcode}` : ""}
      </p>

      {selectedIsUnlocked && (phone || email || fullAddress) && (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {phone && (
            <QuickAction
              icon={Phone}
              label={phone}
              href={`tel:${phone.replace(/\s+/g, "")}`}
            />
          )}
          {email && (
            <QuickAction
              icon={Mail}
              label={email}
              href={`mailto:${email}`}
            />
          )}
          {fullAddress && (
            <QuickAction
              icon={MapPin}
              label={fullAddress}
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`}
              external
            />
          )}
        </div>
      )}
    </div>
  );
}

function QuickAction({ icon: Icon, label, href, external }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "5px 10px",
        borderRadius: "8px",
        background: "var(--bg, rgba(140,100,80,0.06))",
        border: "1px solid var(--border, rgba(140,100,80,0.18))",
        color: "var(--text)",
        fontSize: "0.78rem",
        textDecoration: "none",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={12} aria-hidden="true" style={{ flexShrink: 0, color: "var(--text-soft, #6b5448)" }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    </a>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// NotesSection — internal-only notes the tradie keeps on this lead.
// Auto-saves on blur via onUpdateContactDetails. Sits at the top of the
// Details tab so it's the first thing the tradie sees when opening a card.
// ──────────────────────────────────────────────────────────────────────────
function NotesSection({ lead, onSave }) {
  const initial = lead?.tradieNotes || "";
  const initialPhotos = Array.isArray(lead?.tradieNotesPhotos) ? lead.tradieNotesPhotos : [];
  const [value, setValue] = useState(initial);
  const [photos, setPhotos] = useState(initialPhotos);
  const [savedFlash, setSavedFlash] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const lastSavedRef = useRef(initial);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  useEffect(() => {
    setValue(lead?.tradieNotes || "");
    setPhotos(Array.isArray(lead?.tradieNotesPhotos) ? lead.tradieNotesPhotos : []);
    lastSavedRef.current = lead?.tradieNotes || "";
  }, [lead?.tradieNotes, lead?.tradieNotesPhotos]);

  async function handleBlur() {
    if (!onSave) return;
    if (value === lastSavedRef.current) return;
    const result = await onSave(lead.id, { tradie_notes: value });
    if (result?.ok !== false) {
      lastSavedRef.current = value;
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1400);
    }
  }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    if (!files.length || !onSave) return;
    setUploading(true);

    const next = [...photos];
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        showToast(`Skipped ${file.name} — only image files supported`, "error");
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        showToast(`${file.name} is too large (max 8MB)`, "error");
        continue;
      }
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${lead.id}/notes/${timestamp}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from("chat-media")
        .upload(path, file, { upsert: false });
      if (uploadError) {
        showToast(`Upload failed: ${uploadError.message}`, "error");
        continue;
      }
      const { data: urlData } = supabase.storage.from("chat-media").getPublicUrl(path);
      next.push({
        url: urlData?.publicUrl || "",
        name: file.name,
        size: file.size,
        uploaded_at: new Date().toISOString(),
      });
    }

    setPhotos(next);
    const result = await onSave(lead.id, { tradie_notes_photos: next });
    setUploading(false);
    if (result?.ok === false) {
      showToast("Couldn't save photos — try again", "error");
    } else {
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1400);
    }
  }

  async function removePhoto(idx) {
    if (!onSave) return;
    const next = photos.filter((_, i) => i !== idx);
    setPhotos(next);
    const result = await onSave(lead.id, { tradie_notes_photos: next });
    if (result?.ok === false) {
      showToast("Couldn't remove photo — try again", "error");
      setPhotos(photos);
    }
  }

  if (!onSave) return null;

  return (
    <div
      style={{
        marginBottom: "14px",
        padding: "12px 14px",
        background: "rgba(255, 138, 0, 0.06)",
        border: "1px solid rgba(255, 138, 0, 0.20)",
        borderRadius: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <StickyNote
          size={14}
          aria-hidden="true"
          style={{ color: "var(--primary, #FF8A00)" }}
        />
        <span
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--text-soft, #6b5448)",
          }}
        >
          Internal notes
        </span>
        <span
          style={{
            fontSize: "0.72rem",
            color: "var(--text-faint, #a8897c)",
            fontStyle: "italic",
          }}
        >
          (only you see these)
        </span>
        {savedFlash && (
          <span
            aria-live="polite"
            style={{
              marginLeft: "auto",
              fontSize: "0.72rem",
              color: "#22C55E",
              fontWeight: 600,
            }}
          >
            Saved
          </span>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        rows={3}
        placeholder="Site access, preferred contact times, anything you want to remember about this customer…"
        style={{
          width: "100%",
          padding: "8px 10px",
          fontSize: "0.85rem",
          lineHeight: 1.5,
          borderRadius: "8px",
          border: "1px solid rgba(255, 138, 0, 0.20)",
          background: "var(--surface, #fff)",
          color: "var(--text)",
          fontFamily: "inherit",
          resize: "vertical",
          minHeight: "60px",
        }}
      />

      {photos.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "10px",
          }}
        >
          {photos.map((p, i) => (
            <div
              key={`${p.url}-${i}`}
              style={{
                position: "relative",
                width: "64px",
                height: "64px",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid rgba(255, 138, 0, 0.20)",
                background: "var(--surface, #fff)",
              }}
            >
              <button
                type="button"
                onClick={() => setLightboxIndex(i)}
                style={{
                  width: "100%",
                  height: "100%",
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "zoom-in",
                }}
                aria-label={`Open ${p.name}`}
              >
                <img
                  src={p.url}
                  alt={p.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </button>
              <button
                type="button"
                onClick={() => removePhoto(i)}
                aria-label="Remove photo"
                style={{
                  position: "absolute",
                  top: "2px",
                  right: "2px",
                  width: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <XIcon size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: "8px" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          style={{ display: "none" }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "5px 10px",
            fontSize: "0.78rem",
            borderRadius: "8px",
            border: "1px dashed rgba(255, 138, 0, 0.35)",
            background: "transparent",
            color: "var(--primary, #FF8A00)",
            cursor: uploading ? "wait" : "pointer",
            opacity: uploading ? 0.6 : 1,
          }}
        >
          <ImagePlus size={13} aria-hidden="true" />
          {uploading ? "Uploading…" : "Add photo"}
        </button>
      </div>

      {lightboxIndex >= 0 && photos[lightboxIndex] && (
        <CustomerPhotoLightbox
          photos={photos.map((p) => ({ dataUrl: p.url, name: p.name }))}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CapturedInfo — compact two-column key/value summary of the customer's
// intake form. "Not entered" rows hidden so the panel stays glanceable.
// ──────────────────────────────────────────────────────────────────────────
function CapturedInfo({ lead, onPhotoClick }) {
  const surfaces = (lead.form.surfaces || []).filter(Boolean).join(", ");
  const fullAddress =
    lead.form.address ||
    [lead.form.suburb || lead.suburb, lead.form.postcode || lead.postcode]
      .filter(Boolean).join(" ");

  const rows = [
    { k: "Trade", v: lead.trade },
    { k: "Project", v: lead.type },
    { k: "Urgency", v: lead.form.urgency },
    { k: "Address", v: fullAddress },
    { k: "Suburb", v: `${lead.form.suburb || lead.suburb || ""}${(lead.form.postcode || lead.postcode) ? ` ${lead.form.postcode || lead.postcode}` : ""}` },
    { k: "Surfaces", v: surfaces },
    { k: "Measurements", v: lead.form.measurements },
    { k: "Issue type", v: lead.form.issueType },
    { k: "Service category", v: lead.form.serviceCategory },
    { k: "Access details", v: lead.form.accessDetails },
    { k: "Notes from customer", v: lead.form.notes },
  ].filter((r) => r.v && String(r.v).trim() && String(r.v).trim() !== "Not entered");

  const photos = lead.form.uploadedPhotos || [];

  return (
    <Card>
      <h3 style={{ margin: "0 0 12px", fontSize: "0.95rem" }}>Job summary</h3>
      {rows.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          The customer didn't add any extra detail.
        </p>
      ) : (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: "16px",
            rowGap: "6px",
            margin: 0,
          }}
        >
          {rows.map((r) => (
            <React.Fragment key={r.k}>
              <dt
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  color: "var(--text-soft, #6b5448)",
                  whiteSpace: "nowrap",
                }}
              >
                {r.k}
              </dt>
              <dd
                style={{
                  margin: 0,
                  fontSize: "0.86rem",
                  color: "var(--text)",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}
              >
                {r.v}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      {photos.length > 0 && (
        <>
          <h3 style={{ margin: "16px 0 8px", fontSize: "0.95rem" }}>
            Photos ({photos.length})
          </h3>
          <div className="photo-grid dashboard-photo-grid">
            {photos.map((photo, idx) => (
              <div
                key={`${photo.name}-${idx}`}
                className="uploaded-photo-card"
                onClick={() => onPhotoClick(photos, idx)}
                style={{ cursor: "pointer" }}
              >
                <img
                  src={photo.dataUrl}
                  alt={photo.name}
                  className="uploaded-photo"
                />
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Media tab — grid of saved chat/media items. Uses a two-step "delete"
// confirm so a stray click can't remove a file.
// ──────────────────────────────────────────────────────────────────────────
function MediaTab({ savedMedia, onRemove, onOpenLightbox }) {
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);

  if (savedMedia.length === 0) {
    return (
      <p style={{ color: "var(--text-muted, #6b7280)", fontSize: "0.875rem", textAlign: "center", padding: "32px 0" }}>
        No media saved yet — click images or videos in the Messages tab to add them here.
      </p>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
        gap: "12px",
      }}
    >
      {savedMedia.map((item, idx) => {
        const isImage = item.file_type?.startsWith("image/");
        const isAudio = item.file_type?.startsWith("audio/");
        const isVideo = item.file_type?.startsWith("video/");
        const isConfirming = confirmDeleteIndex === idx;

        return (
          <div
            key={`${item.file_url}-${idx}`}
            style={{
              position: "relative",
              border: "1px solid var(--border, #e5e7eb)",
              borderRadius: "12px",
              overflow: "hidden",
              background: "var(--surface, #fff)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isConfirming ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "18px 12px",
                  minHeight: "120px",
                }}
              >
                <p style={{ margin: 0, fontSize: "0.8rem", fontWeight: 600, color: "var(--text, #1C1C1E)", textAlign: "center" }}>
                  Remove from Media?
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onRemove(idx);
                    setConfirmDeleteIndex(null);
                  }}
                  style={{
                    width: "100%",
                    padding: "6px 0",
                    borderRadius: "8px",
                    border: "none",
                    background: "#EF4444",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    cursor: "pointer",
                  }}
                >
                  Yes, remove
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteIndex(null)}
                  style={{
                    width: "100%",
                    padding: "6px 0",
                    borderRadius: "8px",
                    border: "1px solid var(--border, #e5e7eb)",
                    background: "transparent",
                    color: "var(--text-muted, #6b7280)",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteIndex(idx)}
                  aria-label="Remove media item"
                  style={{
                    position: "absolute",
                    top: "6px",
                    right: "6px",
                    zIndex: 2,
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    fontSize: "0.9rem",
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  ×
                </button>

                {isImage && (
                  <img
                    src={item.file_url}
                    alt={item.file_name}
                    onClick={() => onOpenLightbox(item)}
                    style={{ width: "100%", aspectRatio: "1", objectFit: "cover", cursor: "pointer", display: "block" }}
                  />
                )}
                {isVideo && (
                  <div
                    style={{ position: "relative", aspectRatio: "16/9", background: "#000", cursor: "pointer" }}
                    onClick={() => onOpenLightbox(item)}
                  >
                    <video src={item.file_url} style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#1C1C1E"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </div>
                    </div>
                  </div>
                )}
                {isAudio && (
                  <div style={{ padding: "12px", color: "var(--text-muted, #6b7280)", display: "flex", justifyContent: "center" }}>
                    <IconAudio size={32} />
                  </div>
                )}
                {!isImage && !isVideo && !isAudio && (
                  <div style={{ padding: "12px", color: "var(--text-muted, #6b7280)", display: "flex", justifyContent: "center" }}>
                    <IconFile size={32} />
                  </div>
                )}

                <div style={{ padding: "8px 10px", flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                  {isAudio && (
                    <audio controls src={item.file_url} style={{ width: "100%", marginBottom: "6px" }} />
                  )}
                  {!isImage && !isVideo && !isAudio && (
                    <a href={item.file_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)", textDecoration: "none", display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px", fontSize: "0.8rem", fontWeight: 600 }}>
                      Download
                    </a>
                  )}
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text, #1C1C1E)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.file_name || "File"}
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted, #6b7280)" }}>
                    {item.sender_name}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Main export — renders the right-hand panel of the Pipeline view:
//   • Nothing selected          → prompt to pick a lead
//   • Selected but locked       → LockedLeadPreview (unlock CTA)
//   • Selected + unlocked       → tabs: Details | Quote | Messages | Media
//
// State kept here (local to the selected lead):
//   detailTab, msgCount, savedMedia, customerPhotoLightbox, mediaFileLightbox
//
// Parent (TradieDashboard) owns selectedId and bookJobLead so those pieces
// of state remain available to modals rendered at the top level.
// ──────────────────────────────────────────────────────────────────────────
export default function LeadDetailPanel({
  selected,
  selectedIsUnlocked,
  profile,
  businessId,
  leadsLoading = false,
  subscription,
  unlockLead,
  updateRequestStatus,
  saveQuote,
  getCustomerQuoteLink,
  getCustomerInvoiceLink,
  loadLeadDetails,
  onOpenBookModal,
  onUpdateContactDetails,
  detailTabOverrideRef,
  markAsRead,
  isRead = () => false,
  markMediaSeen,
  isMediaSeen = () => false,
  setShowPricing,
}) {
  const { showToast } = useToast();

  const [detailTab, setDetailTab] = useState("quote");
  const [msgCount, setMsgCount] = useState(0);
  const [savedMedia, setSavedMedia] = useState([]);
  const [customerPhotoLightbox, setCustomerPhotoLightbox] = useState(null); // { photos, index }
  const [mediaFileLightbox, setMediaFileLightbox] = useState(null); // media item

  // Stable primitives extracted so effects don't depend on the `selected` object
  // reference (which is recreated each render via requests.find()).
  const selectedId = selected?.id;
  const selectedDetailsLoaded = selected?.detailsLoaded;
  const selectedCreatedAt = selected?.createdAt;
  const selectedUploadedPhotos = selected?.form?.uploadedPhotos;
  const selectedIsCashQuote = isCashPaymentLead(selected);

  // Fetch lead_details + lead_media + unmasked leads row on demand when an
  // unlocked lead is selected for the first time. detailsLoaded prevents
  // duplicate fetches. See useLeads.loadLeadDetails for what gets merged.
  useEffect(() => {
    if (!selectedId || !selectedIsUnlocked || selectedDetailsLoaded) return;
    loadLeadDetails(selectedId);
  }, [selectedId, selectedDetailsLoaded, selectedIsUnlocked, loadLeadDetails]);

  // Reset tab / load message count when the selected lead changes.
  // If the count query fails (e.g. session expired), surface a toast so the
  // tradie knows instead of silently showing "0 messages".
  useEffect(() => {
    const rawPendingTab = detailTabOverrideRef?.current;
    const pendingTab = rawPendingTab ? normalizeLeadDetailTab(rawPendingTab) : null;
    if (pendingTab) {
      detailTabOverrideRef.current = null;
      setDetailTab(pendingTab);
    } else {
      setDetailTab("quote");
    }
    setMsgCount(0);
    setMediaFileLightbox(null);

    if (!selectedId) return;
    let countQuery = supabase
      .from("lead_messages")
      .select("id", { count: "exact", head: true })
      .eq("lead_id", selectedId);
    if (businessId) {
      countQuery = countQuery.or(`business_id.eq.${businessId},business_id.is.null,sender_role.eq.customer`);
    }
    countQuery.then(({ count, error }) => {
        if (error) {
          showToast(
            `Couldn't load message count — ${error.message || "check your connection"}`,
            "error"
          );
          return;
        }
        setMsgCount(count || 0);
      });
  }, [selectedId, businessId, showToast, detailTabOverrideRef]);

  // Mark lead as read when the messages tab is open
  useEffect(() => {
    if (detailTab === "messages" && selectedId) {
      markAsRead?.(selectedId);
    }
  }, [detailTab, selectedId, markAsRead]);

  // Customer photos live in Overview now, so opening Overview clears the badge.
  useEffect(() => {
    if (detailTab === "overview" && selectedId) {
      markMediaSeen?.(selectedId);
    }
  }, [detailTab, selectedId, markMediaSeen]);

  useEffect(() => {
    if (!selectedId) return;
    try {
      localStorage.setItem(getLeadTabStorageKey(selectedId), detailTab);
    } catch {
      /* storage blocked */
    }
  }, [detailTab, selectedId]);

  useEffect(() => {
    if (selectedIsCashQuote && detailTab === "invoice") {
      setDetailTab("quote");
    }
  }, [selectedIsCashQuote, detailTab]);

  // Merge customer-submitted photos into savedMedia alongside any chat media
  // the tradie has already "saved" from the Messages tab. Runs whenever the
  // customer photo list changes so loadLeadDetails completing after initial
  // render is captured.
  useEffect(() => {
    const leadPhotos = (selectedUploadedPhotos || [])
      .filter((p) => p.dataUrl && (
        p.dataUrl.startsWith("http") ||
        p.dataUrl.startsWith("data:") ||
        p.dataUrl.startsWith("blob:")
      ))
      .map((photo) => ({
        file_url:    photo.dataUrl,
        file_name:   photo.name,
        file_type:   photo.type || "image/jpeg",
        file_size:   photo.size || 0,
        sender_name: "Customer",
        created_at:  selectedCreatedAt || new Date().toISOString(),
      }));

    // Keep any chat media the tradie already saved — don't wipe it on re-run
    setSavedMedia((prev) => {
      const chatMedia = prev.filter(
        (m) => m.sender_name !== "Customer" ||
               (m.file_url?.startsWith("https://") && m.file_url?.includes("chat-media"))
      );
      const merged = [...leadPhotos, ...chatMedia];
      const seen = new Set();
      return merged.filter((m) => {
        if (seen.has(m.file_url)) return false;
        seen.add(m.file_url);
        return true;
      });
    });
  }, [selectedId, selectedUploadedPhotos, selectedDetailsLoaded, selectedCreatedAt]);

  if (!selected) {
    return (
      <Card className="rq-detail-panel-card">
        <p className="muted" style={{ textAlign: "center", padding: "40px 0" }}>
          Select a lead to view details.
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="rq-detail-panel-card">
        <DetailHeader selected={selected} selectedIsUnlocked={selectedIsUnlocked} />

        {!selectedIsUnlocked ? (
          <LockedLeadPreview lead={selected} onUnlock={unlockLead} />
        ) : (
          <>
            {/* ── Detail tab bar ──────────────────────────────── */}
            <div
              className="rq-detail-tabs"
              style={{
                display: "flex",
                gap: "0",
                borderBottom: "2px solid var(--border, #e5e7eb)",
                marginBottom: "18px",
                overflowX: "auto",
              }}
            >
              {[
                { key: "quote",    label: "Quote" },
                { key: "messages", label: "Messages", badge: msgCount },
                { key: "notes",    label: "Notes" },
                ...(!selectedIsCashQuote ? [{ key: "invoice", label: "Invoice" }] : []),
                { key: "activity", label: "Activity" },
                { key: "overview", label: "Overview", badge: savedMedia.length },
              ].map(({ key, label, badge }) => {
                const active = detailTab === key;
                const isMsgTab = key === "messages";
                const msgSeen = isMsgTab && isRead(selected?.id);
                const mediaSeen = key === "overview" && isMediaSeen(selected?.id);
                // Inactive Messages/Media tab gets an orange underline while unread/unseen
                const showsUnreadAccent =
                  (isMsgTab && !msgSeen && badge > 0) ||
                  (key === "overview" && !mediaSeen && badge > 0);
                const underlineColor = active
                  ? "var(--primary)"
                  : showsUnreadAccent
                    ? "var(--primary)"
                    : "transparent";
                // Hide badge entirely once the tab has been opened — the
                // user has acknowledged whatever was new. Other tabs (Quote,
                // Details) never set badge so this branch leaves them alone.
                const badgeMuted = (isMsgTab && msgSeen) || (key === "overview" && mediaSeen);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      if (key === "quote" && subscription?.isJobLimitReached) {
                        setShowPricing(true);
                        return;
                      }
                      setDetailTab(key);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      borderBottom: `2px solid ${underlineColor}`,
                      marginBottom: "-2px",
                      padding: "8px 12px",
                      fontWeight: active ? 700 : 500,
                      fontSize: "0.9rem",
                      color: active ? "var(--primary)" : "var(--text-muted, #6b7280)",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "color 0.15s",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                    {badge > 0 && !badgeMuted && (
                      <span
                        style={{
                          background: "var(--primary)",
                          color: "#fff",
                          borderRadius: "999px",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          padding: "1px 6px",
                          lineHeight: "1.5",
                        }}
                      >
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="rq-detail-scroll-shell">
              <div className="rq-detail-scroll-row" style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
              <div className="rq-detail-tabcard" style={{ flex: 1, minWidth: 0, maxHeight: "calc(100vh - 280px)", overflowY: "auto", overflowX: "hidden" }}>
                {/* Overview tab */}
                {detailTab === "overview" && (
                  <>
                    <CapturedInfo
                      lead={selected}
                      onPhotoClick={(photos, index) =>
                        setCustomerPhotoLightbox({ photos, index })
                      }
                    />
                    {savedMedia.length > 0 && (
                      <MediaTab
                        savedMedia={savedMedia}
                        onRemove={(idx) =>
                          setSavedMedia((prev) => prev.filter((_, i) => i !== idx))
                        }
                        onOpenLightbox={(item) => setMediaFileLightbox(item)}
                      />
                    )}

                    {/* ── Book job date ─────────────────────────── */}
                    {(selected.status || "").toLowerCase() === "accepted" && (
                      <div
                        style={{
                          background: "var(--surface, #fff)",
                          border: "1px solid var(--border, #e5e7eb)",
                          borderLeft: "4px solid #22C55E",
                          borderRadius: "12px",
                          padding: "18px 20px",
                        }}
                      >
                        <h3 style={{ margin: "0 0 12px", fontSize: "1rem" }}>
                          <BadgeCheck size={16} style={{ verticalAlign: "middle", marginRight: "6px", color: "#22C55E" }} />
                          Job scheduling
                        </h3>
                        {selected.jobBooked ? (
                          <div>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#22C55E", color: "#fff", padding: "6px 14px", borderRadius: "999px", fontWeight: 600, fontSize: "0.875rem", marginBottom: "6px" }}>
                              ✓ Job booked
                            </div>
                            <p className="muted small" style={{ margin: "4px 0 0" }}>
                              {formatBookedDate(selected.jobBookedDate)}
                            </p>
                            <button
                              type="button"
                              onClick={() => onOpenBookModal(selected)}
                              style={{ marginTop: "10px", background: "none", border: "1px solid var(--border, #e5e7eb)", borderRadius: "8px", padding: "6px 12px", fontSize: "0.8rem", cursor: "pointer", color: "var(--text-muted, #6b7280)" }}
                            >
                              Change date
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onOpenBookModal(selected)}
                            style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "var(--primary)", color: "#fff", border: "none", borderRadius: "12px", padding: "10px 18px", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer" }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            Book job date
                          </button>
                        )}
                      </div>
                    )}

                    {selected.quote?.deposit_paid && (
                      <Card>
                        <h3>
                          <BadgeCheck size={16} /> Deposit received
                        </h3>
                        <p className="muted">
                          The customer has paid a deposit of{" "}
                          <strong>
                            {formatMoneyDisplay(selected.quote.deposit_amount || 0)}
                          </strong>
                          .
                        </p>
                      </Card>
                    )}
                  </>
                )}

                {/* ── Quote tab ───────────────────────────────────── */}
                {detailTab === "notes" && (
                  <NotesSection
                    lead={selected}
                    onSave={onUpdateContactDetails}
                  />
                )}

                {detailTab === "quote" && (
                  <>
                    {selected?.quote && (
                      <div style={{ marginBottom: "16px" }}>
                        <ActivityTimeline quote={selected.quote} />
                      </div>
                    )}
                    <QuoteBuilder
                      selected={selected}
                      saveQuote={saveQuote}
                      getCustomerQuoteLink={getCustomerQuoteLink}
                      profile={profile}
                    />
                  </>
                )}

                {/* Invoice tab -- create / amend / send invoice for this lead. */}
                {detailTab === "invoice" && !selectedIsCashQuote && (
                  <InvoiceBuilder
                    selected={selected}
                    businessId={businessId}
                    profile={profile}
                    getCustomerInvoiceLink={getCustomerInvoiceLink}
                  />
                )}

                {/* ── Messages tab ────────────────────────────────── */}
                {detailTab === "messages" && (
                  <LeadChat
                    leadId={selected.id}
                    senderRole="tradie"
                    senderName={profile?.full_name || "Tradie"}
                    businessId={businessId}
                    isResolving={!businessId && leadsLoading}
                    markAsRead={markAsRead}
                    onAddToMedia={(item) => {
                      setSavedMedia((prev) => {
                        if (prev.some((m) => m.file_url === item.file_url)) return prev;
                        return [...prev, item];
                      });
                      setDetailTab("overview");
                    }}
                  />
                )}

                {/* Activity tab */}
                {detailTab === "activity" && (
                  selected.quote ? (
                    <ActivityTimeline quote={selected.quote} />
                  ) : (
                    <p className="muted small" style={{ margin: 0 }}>
                      No quote activity yet.
                    </p>
                  )
                )}
              </div>

            </div>
            </div>
          </>
        )}
      </Card>

      {/* Lightboxes rendered at panel level so they overlay everything */}
      {customerPhotoLightbox && (
        <CustomerPhotoLightbox
          photos={customerPhotoLightbox.photos}
          startIndex={customerPhotoLightbox.index}
          onClose={() => setCustomerPhotoLightbox(null)}
        />
      )}

      {mediaFileLightbox && (
        <MediaFileLightbox
          item={mediaFileLightbox}
          onClose={() => setMediaFileLightbox(null)}
        />
      )}
    </>
  );
}
